<template>
  <div
    ref="avatar"
    class="avatar"
    :class="{
      [`avatar--medium`]: typeof size !== 'number' && size === 'medium',
      [`avatar--small`]: typeof size !== 'number' && size === 'small',
      [`avatar--large`]: typeof size !== 'number' && size === 'large',
      [`avatar--xlarge`]: typeof size !== 'number' && size === 'xlarge'
    }"
    :style="{
      height: typeof size === 'number' && size + 'rem',
      width: typeof size === 'number' && size + 'rem'
    }"
    :aria-label="ariaLabel || displayName"
  >
    <div class="avatar__holder" :class="{ 'avatar--circle': circle }">
      <!-- Real image — shown only when a URL is provided and it
           actually loads successfully. On error we fall back to
           initials automatically. -->
      <img
        v-if="hasImage"
        loading="lazy"
        :src="image"
        class="avatar__image"
        :alt="alt || displayName"
        @error="onImageError"
      />
      <!-- Initials placeholder — colored disc with the user's
           initials centered inside. -->
      <span
        v-else
        class="avatar__initials"
        :style="initialsStyle"
        :aria-hidden="!!ariaLabel || !!displayName ? 'false' : 'true'"
        >{{ initials }}</span
      >
    </div>
    <div
      v-if="onlineStatus !== undefined && onlineStatus !== null"
      class="avatar__status"
    >
      <span
        class="status"
        :class="{
          'status--online': onlineStatus === true,
          'status--offline': onlineStatus === false
        }"
      />
    </div>
  </div>
</template>

<script>
// Soft pastel palette — each name hashes onto one of these so the
// same person always gets the same chip color across the app. The
// foreground is a deeper tone of the same hue so initials stay
// legible against the pastel background.
const AVATAR_PALETTE = [
  { bg: '#FFE2E0', fg: '#B23A48' }, // blush pink
  { bg: '#FFE8C7', fg: '#A65B1F' }, // peach
  { bg: '#FFF4C2', fg: '#8A6D00' }, // butter yellow
  { bg: '#DDF3D8', fg: '#2F6B33' }, // mint
  { bg: '#CDEBE5', fg: '#0E6B63' }, // pale teal
  { bg: '#D6EAFE', fg: '#1E4E8C' }, // sky blue
  { bg: '#E1DCF7', fg: '#4B3C8A' }, // lavender
  { bg: '#F6D9EE', fg: '#8A2F6B' }, // pink lilac
  { bg: '#FAD9C1', fg: '#8A4A1F' }, // apricot
  { bg: '#E3E6F0', fg: '#3A4560' } // cool grey
];

function hashString(str) {
  if (!str) return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0; // force 32-bit int
  }
  return Math.abs(h);
}

// The backend (and various third-party services) return generic
// placeholder URLs when the user / speaker / sponsor hasn't uploaded
// a real photo. Detect those so we render proper name-based initials
// instead of the placeholder image (which often has odd baked-in
// text, the wrong initials, or a generic silhouette).
const PLACEHOLDER_IMAGE_PATTERNS = [
  // Tappin's own S3 / asset placeholders
  /ideffector-prod\.s3\.amazonaws\.com\/images\/avatar\.png/i,
  /\/assets\/user-solid\.svg/i,
  /\/images\/avatar\.png$/i,

  // Third-party initials/avatar generators — these render text
  // baked INTO the image, which the user perceives as "wrong
  // initials". Bypass them so the Avatar component's own colored-
  // initials placeholder takes over.
  /ui-avatars\.com/i,
  /avatars?\.dicebear\.com/i,
  /robohash\.org/i,
  /(www\.)?gravatar\.com\/avatar\/[a-f0-9]+\?.*?d=(identicon|monsterid|wavatar|retro|robohash|mp|mm)/i,

  // Generic placeholder image services
  /placehold\.(it|co|jp)/i,
  /(via\.)?placeholder\.com/i,
  /dummyimage\.com/i,
  /fakeimg\.pl/i,

  // Heuristic — any URL whose path contains an obvious placeholder
  // marker. Trailing `[?#]?` allows `?param=…` query strings.
  /\/(default[-_]?avatar|placeholder[-_]?avatar|avatar[-_]?placeholder|avatar[-_]?default|no[-_]?avatar|no[-_]?image|user[-_]?placeholder)\b/i
];

function isPlaceholderImage(url) {
  if (!url) return true;
  const s = String(url).trim();
  if (!s) return true;
  return PLACEHOLDER_IMAGE_PATTERNS.some((rx) => rx.test(s));
}

export default {
  name: 'Avatar',
  props: {
    alt: String,
    ariaLabel: String,
    circle: { type: Boolean, default: false },
    image: String,
    // Optional full name used to derive initials + palette color.
    // Falls back to `alt` or `ariaLabel` for backwards-compat with
    // all existing callers that never passed `name`.
    name: String,
    size: [Number, String], // 'small' | 'medium' | 'large' | 'xlarge' | number (rem)
    onlineStatus: { type: [Boolean, Object], default: null }
  },
  data() {
    return {
      imageFailed: false
    };
  },
  computed: {
    hasImage() {
      return (
        !!this.image && !this.imageFailed && !isPlaceholderImage(this.image)
      );
    },
    // Best-guess name source for initials + color
    displayName() {
      return (this.name || this.alt || this.ariaLabel || '').trim();
    },
    initials() {
      const src = this.displayName;
      if (!src) return '';
      // Split on whitespace and take the first letter of up to two
      // words. "John Doe" → "JD", "Alice" → "A", "Dr. John Smith" → "JS"
      const parts = src
        .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return '';
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      const first = parts[0].charAt(0);
      const last = parts[parts.length - 1].charAt(0);
      return (first + last).toUpperCase();
    },
    palette() {
      const key = this.displayName || this.initials || '';
      return AVATAR_PALETTE[hashString(key) % AVATAR_PALETTE.length];
    },
    initialsStyle() {
      return {
        backgroundColor: this.palette.bg,
        color: this.palette.fg
      };
    }
  },
  watch: {
    image() {
      // Give the new URL a fresh chance — reset the failed flag.
      this.imageFailed = false;
    }
  },
  methods: {
    onImageError() {
      this.imageFailed = true;
    }
  }
};
</script>

<style scoped lang="scss">
.avatar {
  position: relative;
  container-type: inline-size;
  &__holder {
    height: 0;
    padding-bottom: 100%;
    position: relative;
    width: 100%;
  }
  &__status {
    background: var(--background-color);
    border-radius: 50%;
    bottom: 0;
    height: 33%;
    position: absolute;
    right: 0;
    width: 33%;
  }
  &__status-name {
    align-items: center;
    background: #1a202c;
    border-radius: 50%;
    bottom: 0;
    color: #fff;
    display: flex;
    font-size: 13px;
    font-weight: bold;
    justify-content: center;
    left: 0;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: opacity 700ms;
    z-index: 0;
    &:hover {
      opacity: 0.75;
    }
  }
  .status {
    border-radius: 50%;
    height: 75%;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 75%;
    &--online {
      background: var(--color-status-online);
    }
    &--offline {
      background: var(--color-status-offline);
    }
  }
  &--circle {
    border-radius: 50%;
    overflow: hidden;
  }
  &--small {
    height: 1rem;
    width: 1rem;
  }
  &--medium {
    height: 2rem;
    width: 2rem;
  }
  &--large {
    height: 6rem;
    width: 6rem;
  }
  &--xlarge {
    height: 8rem;
    width: 8rem;
  }
  &--full {
    width: 100%;
    &:after {
      position: absolute;
      content: '';
      display: block;
      padding-bottom: 100%;
    }
  }
  &__image {
    border-radius: 50%;
    border: 0;
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    text-align: center;
    text-indent: -10000px;
    top: 0;
    width: 100%;
  }

  // Initials placeholder — covers the holder just like the image.
  &__initials {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI',
      Roboto, sans-serif;
    font-weight: 600;
    // Scale the initials with the avatar's own width via container
    // query units so a 40 px chip and a 120 px profile circle both
    // read cleanly. clamp() keeps the size sensible at the extremes
    // (≥10 px for tiny stack avatars, ≤32 px for large profile shots).
    font-size: clamp(0.625rem, 40cqi, 2rem);
    line-height: 1;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    user-select: none;
    border-radius: 50%;
    background: #e3e6f0;
    color: #3a4560;
    // The border-radius above ensures circles; non-circle callers
    // clip via `.avatar--circle` on the parent holder anyway.
  }
}

// Fallback sizing for browsers that don't support container query
// units (older Safari / Firefox). Maps each size tier to a sensible
// readable rem value.
@supports not (font-size: 1cqi) {
  .avatar {
    &--small .avatar__initials {
      font-size: 0.55rem;
    }
    &--medium .avatar__initials {
      font-size: 0.85rem;
    }
    &--large .avatar__initials {
      font-size: 1.85rem;
    }
    &--xlarge .avatar__initials {
      font-size: 2.4rem;
    }
    // No-tier callers (custom-sized via parent CSS) — modest default.
    &__initials {
      font-size: 0.95rem;
    }
  }
}
</style>
